import { create } from 'zustand';
import moment from 'moment';
import { AssignmentType, TakeoffAssignmentScopeType, TakeoffStatusEnum } from './types';

type ScopeItem = AssignmentType['scope'][number];

type PayloadScopeAT = {
  scope: ScopeItem[];
  start_date: string | null;
  end_date: string | null;
  estimated_minutes: number;
};

type PayloadAssignmentAT = { [assignmentId: string]: PayloadScopeAT };

export type PayloadType = {
  [takeoffId: string]: PayloadAssignmentAT;
};

export type AssignmentState = AssignmentType & {
  isChanged: boolean; // Track whether this assignment was modified
};

type TradeState = {
  tradeName: string;
  takeoffId: string;
  status: TakeoffStatusEnum;
  assignments: AssignmentState[];
};

type ScopeStoreState = {
  trades: Record<string, TradeState>; // trade_id -> TradeState
  selectedTrade: string;
  setInitialAssignments: (assignments: TakeoffAssignmentScopeType, user: { id: string }) => void;
  setAssignment: (tradeId: string, assignmentId: string, data: Partial<AssignmentState>) => void;
  setSelectedTrade: (val: string) => void;
  addScopeItem: (tradeId: string, assignmentId: string, task: string) => void;
  updateScopeItem: (tradeId: string, assignmentId: string, index: number, updatedItem: Partial<ScopeItem>) => void;
  getChangedAssignments: () => PayloadType;
  resetStore: () => void;
};

export const useScopeStore = create<ScopeStoreState>((set, get) => ({
  trades: {},
  selectedTrade: '',

  // Set initial assignments structured by trade_id
  setInitialAssignments: (data, user) => {
    const tradeMap: Record<string, TradeState> = {};

    data.forEach(({ trade_id, trade_name, takeoff_id, status, assignments }) => {
      tradeMap[trade_id] = {
        tradeName: trade_name,
        takeoffId: takeoff_id,
        status,
        assignments: assignments.map(assignment => ({
          ...assignment,
          estimated_minutes: Number(assignment.estimated_minutes),
          isChanged: false
        }))
      };
    });

    const authenticatedUserTrade = data.find(item =>
      item.assignments?.find(ass => ass.user?.id === user?.id)
    )?.trade_id;

    set({ trades: tradeMap, selectedTrade: authenticatedUserTrade ?? data[0].trade_id });
  },

  // Update an entire assignment or specific fields
  setAssignment: (tradeId, assignmentId, data) => {
    set(state => ({
      trades: {
        ...state.trades,
        [tradeId]: {
          ...state.trades[tradeId],
          assignments: state.trades[tradeId].assignments.map(assignment =>
            assignment.id === assignmentId ? { ...assignment, ...data, isChanged: true } : assignment
          )
        }
      }
    }));
  },

  // Update selected trade
  setSelectedTrade: trade => {
    set(state => ({ ...state, selectedTrade: trade }));
  },

  // Add a new scope item to an assignment
  addScopeItem: (tradeId, assignmentId, task) =>
    set(state => ({
      trades: {
        ...state.trades,
        [tradeId]: {
          ...state.trades[tradeId],
          assignments: state.trades[tradeId].assignments.map(assignment =>
            assignment.id === assignmentId
              ? {
                  ...assignment,
                  total_scope_items: assignment.total_scope_items + 1,
                  scope: [{ task, completed: false, reviewed: false }, ...assignment.scope],
                  isChanged: true
                }
              : assignment
          )
        }
      }
    })),

  // Update a specific scope item inside an assignment
  updateScopeItem: (tradeId, assignmentId, index, updatedItem) =>
    set(state => ({
      trades: {
        ...state.trades,
        [tradeId]: {
          ...state.trades[tradeId],
          assignments: state.trades[tradeId].assignments.map(assignment => {
            if (assignment.id === assignmentId) {
              const updatedScope = assignment.scope.map((item, i) =>
                i === index ? { ...item, ...updatedItem } : item
              );
              return {
                ...assignment,
                scope: updatedScope,
                completed_scope_items: updatedScope.reduce((prev, curr) => prev + (curr.completed ? 1 : 0), 0),
                reviewed_scope_items: updatedScope.reduce((prev, curr) => prev + (curr.reviewed ? 1 : 0), 0),
                isChanged: true
              };
            }
            return assignment;
          })
        }
      }
    })),

  // Get only changed assignments across all trades
  getChangedAssignments: () => {
    const payload: PayloadType = {};

    Object.values(get().trades).forEach(trade => {
      const changedAssignments: PayloadAssignmentAT = {};
      trade.assignments.forEach(assignment => {
        if (assignment.isChanged) {
          changedAssignments[assignment.id] = {
            scope: assignment.scope,
            start_date: assignment.start_date ? moment(assignment.start_date).format('YYYY-MM-DD') : null,
            end_date: assignment.end_date ? moment(assignment.end_date).format('YYYY-MM-DD') : null,
            estimated_minutes: assignment.estimated_minutes
          };
        }
      });
      if (Object.keys(changedAssignments).length) {
        payload[trade.takeoffId] = changedAssignments;
      }
    });

    return payload;
  },

  // Reset the store to its initial empty state
  resetStore: () => {
    set({ trades: {}, selectedTrade: '' });
  }
}));
